<script>
  import { useForm } from "@inertiajs/svelte"
  import { totalCartItems, addedToCartDialog, merchant } from "@store"
  import { toHuman } from "@helpers/formatters/currency"
  import { renderJsonLd } from "@helpers/json-ld"
  import route from "@helpers/route"
  import plural from "@helpers/plural"
  import Wrap from "@objects/Wrap.svelte"
  import ContentContainer from "@objects/ContentContainer.svelte"
  import { Page } from "@objects/Page"
  import ProtectedImage from "@components/ProtectedImage.svelte"
  import ThumbnailGallery from "@components/ThumbnailGallery.svelte"
  import SubmitButton from "@components/SubmitButton.svelte"
  import AddedToCartDialog from "../AddedToCartDialog.svelte"
  import { onMount } from "svelte"
  import AffirmPromotionalMessaging, { AffirmPageType } from "@components/AffirmPromotionalMessaging.svelte"
  import ProductAvailability from "@components/ProductAvailability.svelte"
  import { getUnitPriceSuffix } from "@helpers/models/product"
  import Link from "@components/Link.svelte"

  export let collection
  export let product
  export let productJsonLd
  export let quantityInCart

  $: isSampleRequest = product.selection.is_sample_request && product.is_sampleable
  $: unitPriceInCents = isSampleRequest ? product.sample_price_in_cents : product.retail_price_with_shipping_in_cents
  $: unitPriceSuffix = isSampleRequest ? "/ea" : getUnitPriceSuffix(product)

  let featuredImage = product.images.find((image) => image.is_featured)

  const colors = product.colors.map((color) => color.name).join(", ")

  const materials = product.materials.map((material) => material.name).join(", ")

  const form = useForm({
    collection_id: collection.id,
    product_id: product.id,
  })

  onMount(() => {
    addedToCartDialog.close()
  })

  function submit(event) {
    $form.post(event.currentTarget.action, {
      preserveScroll: true,
      onSuccess: ({ props }) => {
        $totalCartItems++
        addedToCartDialog.open(props.flash.item)
      },
    })
  }
</script>

<svelte:head>
  <title>{product.name}</title>

  {@html renderJsonLd(productJsonLd)}
</svelte:head>

<AddedToCartDialog />

<Page>
  <header class="text-center mb-16">
    <Wrap>
      <ContentContainer>
        <Link href={route("shop.collections.show", [collection, collection.slug])}>&larr; Back to collection</Link>
        <strong class="block font-functional text-4xl tracking-tighter font-semibold">
          {collection.name}
        </strong>
      </ContentContainer>
    </Wrap>
  </header>

  <Wrap>
    <ContentContainer>
      <header class="block lg:hidden text-center mb-8">
        {#if collection.settings.shows_vendor_name}
          <span class="text-2xl font-semibold tracking-tight">{product.vendor.name}</span>
        {/if}

        <h1 class="text-5xl font-semibold">{product.name}</h1>
      </header>

      <section class="grid lg:gap-12 lg:grid-cols-2">
        <section class="relative shrink-0 md:aspect-[4/3]">
          <div class="absolute inset-0 z-overlay" />

          {#if featuredImage}
            <ProtectedImage
              src={featuredImage.url}
              alt="Preview image for {product.name}"
              aspect="square"
              class="rounded h-full max-w-full mx-auto"
              width="1024"
            />
          {/if}
        </section>

        <section class="flex flex-col">
          <header class="hidden lg:block">
            <h1 class="text-5xl lg:text-6xl font-semibold">
              {product.name}
            </h1>

            {#if collection.settings.shows_vendor_name}
              <span class="text-xl font-semibold tracking-tight mt-2">{product.vendor.name}</span>
            {/if}
          </header>

          <ThumbnailGallery bind:selected={featuredImage} images={product.images} class="[ mt-4 mb-6 ]" />

          <div class="flex flex-col md:flex-row md:gap-4 items-baseline">
            <div class="font-semibold tracking-tighter text-5xl">
              {toHuman(unitPriceInCents * 0.01)}{unitPriceSuffix}
            </div>

            {#if $merchant.show_affirm_product_promotional_messaging && !collection.settings.hide_affirm_product_promotional_messaging}
              <AffirmPromotionalMessaging
                class="font-sans"
                amountInCents={unitPriceInCents}
                pageType={AffirmPageType.Search}
              />
            {/if}
          </div>

          <ProductAvailability
            class="mt-2"
            {product}
            {collection}
            sample={isSampleRequest}
            pillSize="small"
            buttonSize="small"
          />

          <div class="my-4">
            <form action={route("shop.checkout.cart-items.store")} on:submit|preventDefault={submit}>
              <SubmitButton
                size="large"
                disabled={!product.is_purchasable}
                processing={$form.processing}
                class="font-semibold tracking-tight w-full"
              >
                <span class="sr-only">Add this product to your cart</span>
                <span aria-hidden="true">Add to Cart</span>
              </SubmitButton>

              {#if quantityInCart}
                <p class="mt-1 font-semibold">
                  You have {quantityInCart}
                  {plural("item", quantityInCart)} in your cart
                </p>
              {/if}
            </form>
          </div>
        </section>
      </section>
    </ContentContainer>
  </Wrap>

  <Wrap size="medium" class="mt-16">
    <ContentContainer>
      <section class="card bg-themebg-50 p-8 my-8">
        <h2 class="text-3xl mb-6">Product information</h2>

        <dl class="grid md:grid-cols-2 gap-6">
          {#if product.dimensions}
            <div class="flex flex-col leading-tight">
              <dt class="text-base font-semibold mb-1">Dimensions</dt>
              <dd class="text-lg font-normal">
                {product.dimensions}
              </dd>
            </div>
          {/if}

          {#if product.weight}
            <div class="flex flex-col leading-tight">
              <dt class="text-base font-semibold mb-1">Weight</dt>
              <dd class="text-lg font-normal">{product.weight}lbs</dd>
            </div>
          {/if}

          {#if colors}
            <div class="flex flex-col leading-tight">
              <dt class="text-base font-semibold mb-1">Colors</dt>
              <dd class="text-lg font-normal">{colors}</dd>
            </div>
          {/if}

          {#if materials}
            <div class="flex flex-col leading-tight">
              <dt class="text-base font-semibold mb-1">Materials</dt>
              <dd class="text-lg font-normal">
                {materials}
              </dd>
            </div>
          {/if}
        </dl>
      </section>

      {#if product.description}
        <div>
          {product.description}
        </div>
      {/if}
    </ContentContainer>
  </Wrap>
</Page>
